<template>
  <div class="relative w-full h-full">
    <!-- Header -->
    <div class="relative pb-32">
      <div class="px-4 pt-3 pb-3 bg-white rounded-lg shadow-xl overflow-hidden md:px-10 mx-auto w-full">
        <div class="flex justify-between items-center mb-4">
          <div>
            <h1 class="text-4xl font-bold">Виды услуг пилотов</h1>
            <p class="text-blueGray-400 mt-2">Управление видами услуг для объявлений и профилей пилотов</p>
          </div>
          <button
            @click="openCreateModal"
            class="px-6 py-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition font-bold text-sm uppercase"
          >
            + Добавить услугу
          </button>
        </div>
        <div class="flex gap-2">
          <select
            v-model="sortField"
            @change="sortServices"
            class="border px-3 py-2 pr-7 rounded whitespace-nowrap"
          >
            <option value="name">По названию {{ sortOrder === 'asc' ? '↑' : '↓' }}</option>
          </select>
          <button
            @click="toggleSortOrder"
            class="border px-3 py-2 rounded bg-gray-100 hover:bg-gray-200 whitespace-nowrap"
          >
            {{ sortOrder === 'asc' ? '↑ Возрастание' : '↓ Убывание' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="mx-auto w-full -mt-24 mb-12 relative z-index-10">
      <div class="bg-white rounded-lg shadow-xl p-8">
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-12">
          <p class="text-blueGray-500">Загрузка...</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="services.length === 0 && inactiveServices.length === 0" class="text-center py-12">
          <p class="text-blueGray-500 mb-4">Нет добавленных услуг</p>
          <button
            @click="openCreateModal"
            class="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition"
          >
            Создать первую услугу
          </button>
        </div>

        <div v-if="inactiveServices.length > 0" class="mb-8">
          <h3 class="text-xl font-bold mb-4 text-amber-700">⚠️ На подтверждение</h3>
          <div class="overflow-x-auto">
            <table class="w-full border-collapse">
              <thead>
                <tr class="bg-amber-50 border-b border-amber-200">
                  <th class="px-6 py-3 text-left text-sm font-bold text-amber-700">Название</th>
                  <th class="px-6 py-3 text-left text-sm font-bold text-amber-700">Описание</th>
                  <th class="px-6 py-3 text-right text-sm font-bold text-amber-700">Действия</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="service in inactiveServices"
                  :key="service.id"
                  class="border-b border-amber-100 hover:bg-amber-50 transition"
                >
                  <td class="px-6 py-4 text-sm text-amber-900 font-medium">{{ service.name }}</td>
                  <td class="px-6 py-4 text-sm text-amber-800">{{ service.description || '—' }}</td>
                  <td class="px-6 py-4 text-sm text-right">
                    <button
                      @click="approveService(service)"
                      class="px-3 py-1 bg-emerald-500 text-white rounded hover:bg-emerald-600 transition text-xs font-semibold mr-2"
                    >
                      ✓ Подтвердить
                    </button>
                    <button
                      @click="deleteService(service.id)"
                      class="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition text-xs font-semibold"
                    >
                      Удалить
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Active Services Section -->
        <div v-if="services.length > 0">
          <h3 v-if="inactiveServices.length > 0" class="text-xl font-bold mb-4 text-blueGray-700">✓ Подтвержденные услуги</h3>
          <div class="overflow-x-auto">
          <table class="w-full border-collapse">
            <thead>
              <tr class="bg-blueGray-50 border-b border-blueGray-200">
                <th class="px-6 py-3 text-left text-sm font-bold text-blueGray-700">Название</th>
                <th class="px-6 py-3 text-left text-sm font-bold text-blueGray-700">Ключ</th>
                <th class="px-6 py-3 text-left text-sm font-bold text-blueGray-700">Статус</th>
                <th class="px-6 py-3 text-left text-sm font-bold text-blueGray-700">Порядок</th>
                <th class="px-6 py-3 text-right text-sm font-bold text-blueGray-700">Действия</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="service in services"
                :key="service.id"
                class="border-b border-blueGray-200 hover:bg-blueGray-50 transition"
              >
                <td class="px-6 py-4 text-sm text-blueGray-700 font-medium">{{ service.name }}</td>
                <td class="px-6 py-4 text-sm text-blueGray-600">
                  <code class="bg-blueGray-100 px-2 py-1 rounded text-xs">{{ service.key }}</code>
                </td>
                <td class="px-6 py-4 text-sm">
                  <span
                    :class="[
                      'px-3 py-1 rounded-full text-xs font-semibold',
                      service.is_active
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-amber-100 text-amber-700'
                    ]"
                  >
                    {{ service.is_active ? '✓ Активна' : '⊘ Неактивна' }}
                  </span>
                </td>
                <td class="px-6 py-4 text-sm text-blueGray-600">{{ service.order }}</td>
                <td class="px-6 py-4 text-sm text-right">
                  <button
                    @click="openEditModal(service)"
                    class="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition text-xs font-semibold mr-2"
                  >
                    Редактировать
                  </button>
                  <button
                    @click="deleteService(service.id)"
                    class="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition text-xs font-semibold"
                  >
                    Удалить
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 p-6">
        <h2 class="text-2xl font-bold mb-4">{{ editingService ? 'Редактировать услугу' : 'Создать услугу' }}</h2>

        <form @submit.prevent="saveService">
          <!-- Name -->
          <div class="mb-4">
            <label class="block text-blueGray-700 text-sm font-bold mb-2">Название *</label>
            <input
              v-model="form.name"
              type="text"
              required
              class="w-full px-4 py-2 border border-blueGray-300 rounded-lg text-blueGray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Например: Аэро Фото/Видеосъемка"
            />
          </div>

          <!-- Key -->
          <div class="mb-4">
            <label class="block text-blueGray-700 text-sm font-bold mb-2">Ключ (ID) *</label>
            <input
              v-model="form.key"
              type="text"
              required
              :disabled="editingService"
              class="w-full px-4 py-2 border border-blueGray-300 rounded-lg text-blueGray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-blueGray-100"
              placeholder="Например: aerial_photo"
            />
            <p class="text-xs text-blueGray-500 mt-1">snake_case формат, не может изменяться</p>
          </div>

          <!-- Description -->
          <div class="mb-4">
            <label class="block text-blueGray-700 text-sm font-bold mb-2">Описание</label>
            <textarea
              v-model="form.description"
              rows="3"
              class="w-full px-4 py-2 border border-blueGray-300 rounded-lg text-blueGray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Дополнительное описание услуги"
            ></textarea>
          </div>

          <!-- Order -->
          <div class="mb-4">
            <label class="block text-blueGray-700 text-sm font-bold mb-2">Порядок отображения</label>
            <input
              v-model.number="form.order"
              type="number"
              class="w-full px-4 py-2 border border-blueGray-300 rounded-lg text-blueGray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="0"
            />
          </div>

          <!-- Is Active -->
          <div class="mb-6">
            <label class="flex items-center">
              <input
                v-model="form.is_active"
                type="checkbox"
                class="w-4 h-4 rounded"
              />
              <span class="ml-2 text-blueGray-700 text-sm font-medium">Активна</span>
            </label>
          </div>

          <!-- Buttons -->
          <div class="flex gap-3">
            <button
              type="submit"
              :disabled="saving"
              class="flex-1 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50 transition font-bold text-sm uppercase"
            >
              {{ saving ? 'Сохранение...' : 'Сохранить' }}
            </button>
            <button
              type="button"
              @click="closeModal"
              class="flex-1 px-4 py-2 bg-blueGray-300 text-blueGray-800 rounded-lg hover:bg-blueGray-400 transition font-bold text-sm uppercase"
            >
              Отмена
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_PILOT_SERVICES, HEADERS_TOKEN_JSON } from '@/const/uri';

export default {
  name: 'PilotServicesIndex',
  data() {
    return {
      services: [],
      inactiveServices: [],
      loading: false,
      saving: false,
      showModal: false,
      editingService: null,
      sortField: 'name',
      sortOrder: 'asc',
      form: {
        name: '',
        key: '',
        description: '',
        order: 0,
        is_active: true,
      },
    };
  },
  mounted() {
    this.fetchServices();
  },
  methods: {
    toggleSortOrder() {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      this.sortServices();
    },
    sortServices() {
      const sortFunc = (a, b) => {
        let aValue = a[this.sortField] || '';
        let bValue = b[this.sortField] || '';

        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (this.sortOrder === 'asc') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
      };

      this.services = this.services.sort(sortFunc);
      this.inactiveServices = this.inactiveServices.sort(sortFunc);
    },
    async fetchServices() {
      this.loading = true;
      try {
        const response = await axios.get(API_PILOT_SERVICES.list, {
          headers: HEADERS_TOKEN_JSON(),
        });

        const allServices = response.data.data || [];
        this.services = allServices
          .filter(s => s.is_active)
          .sort((a, b) => a.order - b.order);
        this.inactiveServices = allServices
          .filter(s => !s.is_active)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        this.sortServices();
      } catch (error) {
        console.error('Error fetching services:', error);
        alert('Ошибка при загрузке услуг');
      } finally {
        this.loading = false;
      }
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    async approveService(service) {
      if (!confirm(`Подтвердить услугу "${service.name}"?`)) {
        return;
      }

      try {
        await axios.patch(
          `${API_PILOT_SERVICES.base}${service.id}/`,
          { is_active: true },
          { headers: HEADERS_TOKEN_JSON() }
        );
        alert('Услуга подтверждена');
        await this.fetchServices();
      } catch (error) {
        console.error('Error approving service:', error);
        alert('Ошибка при подтверждении услуги');
      }
    },

    openCreateModal() {
      this.editingService = null;
      this.form = {
        name: '',
        key: '',
        description: '',
        order: 0,
        is_active: true,
      };
      this.showModal = true;
    },

    openEditModal(service) {
      this.editingService = service;
      this.form = {
        name: service.name,
        key: service.key,
        description: service.description || '',
        order: service.order || 0,
        is_active: service.is_active,
      };
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.editingService = null;
    },

    async saveService() {
      if (!this.form.name || !this.form.key) {
        alert('Заполните все обязательные поля');
        return;
      }

      this.saving = true;
      try {
        const payload = {
          name: this.form.name,
          description: this.form.description,
          order: this.form.order,
          is_active: this.form.is_active,
        };

        if (this.editingService) {
          // Update existing
          await axios.patch(
            `${API_PILOT_SERVICES.base}${this.editingService.id}/`,
            payload,
            { headers: HEADERS_TOKEN_JSON() }
          );
          console.log('Service updated');
        } else {
          // Create new
          payload.key = this.form.key;
          await axios.post(
            API_PILOT_SERVICES.create,
            payload,
            { headers: HEADERS_TOKEN_JSON() }
          );
          console.log('Service created');
        }

        this.closeModal();
        await this.fetchServices();
        alert('Услуга успешно сохранена');
      } catch (error) {
        console.error('Error saving service:', error);
        console.error('Error response:', error.response?.data);
        alert('Ошибка при сохранении услуги. Проверьте данные и попробуйте снова.');
      } finally {
        this.saving = false;
      }
    },

    async deleteService(serviceId) {
      if (!confirm('Вы уверены? Услуга будет удалена.')) {
        return;
      }

      try {
        await axios.delete(
          `${API_PILOT_SERVICES.base}${serviceId}/`,
          { headers: HEADERS_TOKEN_JSON() }
        );
        alert('Услуга удалена');
        await this.fetchServices();
      } catch (error) {
        console.error('Error deleting service:', error);
        alert('Ошибка при удалении услуги');
      }
    },
  },
};
</script>

<style scoped>
</style>
